import React from 'react';
import { Brain, ShieldCheck, RotateCcw, Upload, FlaskConical, AlertTriangle, Power, Eye, SlidersHorizontal, Database, FileText, GitCompare, Info, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGet, apiPost, apiPatch, apiPut } from '../../hooks/useApi.js';
import { formatHours } from '../../lib/format.js';

/**
 * AI Training & Tuning console (v1.16.0 redesign).
 *
 * Four task-oriented tabs:
 *  - Overview: plain-English "what the AI is allowed to do right now", per
 *    capability, plus a prominent master kill switch (real: board policies
 *    killSwitch + calls settings killSwitch).
 *  - Prompts: versioned prompt templates (real storage via ai-config
 *    versions), side-by-side compare, draft/publish/rollback with the
 *    existing governed endpoints. Honest limitation notice: published prompt
 *    templates are NOT yet consumed by the live triage/copilot runtimes, and
 *    no dry-run endpoint exists — both are shown as Unavailable, not faked.
 *  - Settings: every tuning threshold in one place — per-board mode +
 *    autoThreshold, call match ladder (decisive/recommend/floor/margin).
 *  - Data: golden sets, corrections queue, ConnectWise resolution history,
 *    suggested rule changes (the existing content, reorganized).
 */

type Version = { id: string; version: number; status: string; modelProvider: string; modelName: string; promptTemplate?: string; changeSummary: string; createdAt: string; publishedAt?: string; parentVersionId?: string };
type Eval = { id: string; configVersionId: string; sampleSize: number; metrics: Record<string, number>; calibration: unknown; drift: { requiresReview?: boolean }; createdAt: string };
type Correction = { id: string; sourceType: 'triage_review' | 'dispatch_override' | 'call_approval'; kind?: string; confidence?: number; reason?: string; overrideReason?: string; status?: string; ticketCache?: { cwTicketId: number; boardName?: string }; redactedPreview?: string };
type TimeCandidate = { qualityWeight: number; redactedInput: { ticket: { cwTicketId: number; summary: string; board?: string; company?: string; category: string[]; status?: string; resolved: boolean }; chronology: Array<{ cwTimeEntryId: number; at: string; classification: string; internal: boolean; text: string; billable: boolean; observedHours?: number; technician?: { identifier?: string } }> }; sourceTrace: { cwTimeEntryIds: number[]; dateRange: { from?: string; to?: string }; retentionDays: number; expiresAt: string }; correctnessWarning: string };
type Overview = { versions: Version[]; evaluations: Eval[]; suggestions: Array<{ id: string; capability: string; suggestion: string; boardName?: string }>; corrections: Correction[]; goldenCount: number };
type BoardPolicy = { id: string; boardName: string; mode: 'shadow' | 'recommend' | 'auto'; enabled: boolean; autoThreshold: number; updatedAt: string };
type CallSettings = { mode: 'shadow' | 'recommend' | 'auto'; killSwitch: boolean; autoThreshold?: number; matchDecisiveThreshold?: number; matchRecommendThreshold?: number; matchReviewFloor?: number; matchMargin?: number };

const labels: Record<string, string> = { title_rewrite: 'Title rewrite', category: 'Category', priority: 'Priority', summary: 'Summary', actions: 'Actions', technician_recommendation: 'Technician recommendation', call_matching: 'Call matching' };

const TABS = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'prompts', label: 'Prompts', icon: FileText },
  { id: 'settings', label: 'Settings', icon: SlidersHorizontal },
  { id: 'data', label: 'Data', icon: Database },
] as const;
type TabId = (typeof TABS)[number]['id'];

const MODE_COPY: Record<string, string> = {
  shadow: 'Shadow — the AI analyzes and records suggestions but changes nothing on its own. Every outcome needs a human.',
  recommend: 'Recommend — the AI proposes actions (assignments, notes, time drafts) and a human approves each one before it lands in ConnectWise.',
  auto: 'Auto — for this board the AI may apply high-confidence actions itself (above the threshold below). Everything is audit-logged and reversible.',
};

export function AiTrainingPage() {
  const [tab, setTab] = React.useState<TabId>('overview');
  const { data, loading, error, refetch } = useGet<Overview>('/api/v1/ai-training/overview');
  const boards = useGet<{ items: BoardPolicy[] }>('/api/v1/boards/policies');
  const calls = useGet<CallSettings>('/api/v1/calls/settings');

  if (loading && !data) return <div className="p-6">Loading governed AI training…</div>;
  if (error) return <div className="p-6 text-red-600">Failed to load: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Brain className="h-5 w-5" />AI Training &amp; Tuning</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-1">Governed prompt and playbook improvement from approved operational feedback. No autonomous fine-tuning or raw transcript training.</p>
        </div>
      </header>

      <div role="tablist" aria-label="AI console sections" className="flex flex-wrap gap-2 border-b border-[hsl(var(--border))] pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === t.id ? 'bg-brand-600 text-white' : 'text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-hover))]'}`}
          >
            <t.icon className="h-4 w-4" aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab data={data} boards={boards} calls={calls} onChanged={async () => { await Promise.all([boards.refetch(), calls.refetch()]); }} />}
      {tab === 'prompts' && <PromptsTab data={data} refetch={refetch} />}
      {tab === 'settings' && <SettingsTab boards={boards} calls={calls} />}
      {tab === 'data' && <DataTab data={data} refetch={refetch} />}
    </div>
  );
}

/* ------------------------------ Overview ------------------------------ */

interface BoardsQuery { data: { items: BoardPolicy[] } | undefined; loading: boolean; error?: string; refetch: () => unknown }
interface CallsQuery { data: CallSettings | undefined; loading: boolean; error?: string; refetch: () => unknown }
function OverviewTab({ data, boards, calls, onChanged }: {
  data: Overview | undefined;
  boards: BoardsQuery;
  calls: CallsQuery;
  onChanged: () => unknown;
}) {
  const [busy, setBusy] = React.useState(false);
  const policies = boards.data?.items ?? [];
  const callSettings = calls.data;
  const anyLive = policies.some((p) => p.mode !== 'shadow' && p.enabled) || (callSettings && callSettings.mode !== 'shadow' && !callSettings.killSwitch);
  const champion = data?.versions.find((v) => v.status === 'published');

  async function masterKill() {
    if (!confirm('Stop all autonomous AI actions? This sets every board policy kill switch AND the call-intelligence kill switch. Suggestions keep working; nothing is applied without you.')) return;
    setBusy(true);
    const failures: string[] = [];
    try {
      for (const p of policies) {
        if (p.enabled) {
          try { await apiPatch(`/api/v1/boards/policies/${p.id}`, { enabled: false }); } catch { failures.push(p.boardName || p.id); }
        }
      }
      if (callSettings && !callSettings.killSwitch) {
        try { await apiPut('/api/v1/calls/settings', { killSwitch: true }); } catch { failures.push('Call intelligence'); }
      }
      if (failures.length) toast.error('Kill switch applied except: ' + failures.join(', '));
      else toast.success('Master kill switch engaged — all AI actions require a human.');
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <section aria-label="Master kill switch" className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${anyLive ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : 'border-green-300 bg-green-50 dark:bg-green-950/20'}`}>
        <Power className={`h-6 w-6 shrink-0 ${anyLive ? 'text-red-600' : 'text-green-600'}`} aria-hidden="true" />
        <div className="flex-1 min-w-64">
          <h2 className="font-semibold">Master kill switch</h2>
          <p className="text-sm text-[hsl(var(--content-subtle))]">
            {anyLive
              ? 'At least one capability is in recommend/auto mode. Engaging the kill switch forces everything to shadow: the AI keeps analyzing, but can never act on its own.'
              : 'All capabilities are in shadow mode (or killed). The AI cannot take any action without a human.'}
          </p>
        </div>
        <button
          onClick={masterKill}
          disabled={busy || boards.loading || !anyLive}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Stopping…' : 'Engage kill switch'}
        </button>
      </section>

      <section aria-label="What the AI is allowed to do" className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">What the AI is allowed to do right now</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">Plain English, per capability. Mode and kill-switch state come live from board policies and call-intelligence settings.</p>
        </div>
        {boards.loading ? (
          <div className="p-4 text-sm text-[hsl(var(--content-subtle))]">Loading capability states…</div>
        ) : boards.error ? (
          <div className="p-4 text-sm text-red-600">Capability states unavailable: {boards.error} <button className="underline" onClick={() => void boards.refetch()}>Retry</button></div>
        ) : (
          <div className="divide-y">
            {policies.map((p) => (
              <div key={p.id} className="p-4" data-testid={`capability-${p.boardName}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-sm">{p.boardName || 'Board'} — triage, matching &amp; next steps</h3>
                  <ModeChip mode={p.enabled ? p.mode : 'shadow'} killed={!p.enabled} />
                </div>
                <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">{MODE_COPY[p.enabled ? p.mode : 'shadow']}</p>
                {!p.enabled && <p className="mt-1 text-xs text-red-600">Kill switch engaged for this board.</p>}
                {p.enabled && p.mode === 'auto' && (
                  <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">Auto-actions only above <strong>{Math.round(p.autoThreshold * 100)}%</strong> confidence (adjust in Settings).</p>
                )}
              </div>
            ))}
            <div className="p-4" data-testid="capability-call-intelligence">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-sm">Calls — transcription, ticket matching &amp; note drafts</h3>
                <ModeChip mode={callSettings && !callSettings.killSwitch ? callSettings.mode : 'shadow'} killed={!!callSettings?.killSwitch} />
              </div>
              <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">{MODE_COPY[callSettings && !callSettings.killSwitch ? callSettings.mode : 'shadow']} Call notes are always reviewed in the approvals queue before reaching ConnectWise.</p>
              {callSettings?.killSwitch && <p className="mt-1 text-xs text-red-600">Kill switch engaged for call intelligence.</p>}
            </div>
            <div className="p-4" data-testid="capability-summaries">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-sm">Summaries &amp; suggestions</h3>
                <ModeChip mode="shadow" killed={false} />
              </div>
              <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">Ticket summaries, solution suggestions and time analysis are informational only — a human applies every change via the review buttons on the ticket.</p>
            </div>
          </div>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <Stat label="Champion prompt" value={champion ? 'v' + champion.version : 'Not published'} />
        <Stat label="Model" value={champion?.modelName ?? '—'} />
        <Stat label="Golden examples" value={String(data?.goldenCount ?? 0)} />
        <Stat label="Corrections waiting" value={String(data?.corrections.length ?? 0)} />
      </section>
    </div>
  );
}

function ModeChip({ mode, killed }: { mode: string; killed: boolean }) {
  const cls = killed
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : mode === 'auto'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : mode === 'recommend'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{killed ? 'killed' : mode}</span>;
}

/* ------------------------------- Prompts ------------------------------- */

function PromptsTab({ data, refetch }: { data: Overview | undefined; refetch: () => unknown }) {
  const champion = data?.versions.find((v) => v.status === 'published');
  const [editing, setEditing] = React.useState(false);
  const [prompt, setPrompt] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [compareA, setCompareA] = React.useState<string | null>(null);
  const [compareB, setCompareB] = React.useState<string | null>(null);

  function startEdit(base?: Version) {
    setEditing(true);
    setPrompt(base?.promptTemplate ?? '');
    setSummary('');
  }

  async function saveDraft() {
    setBusy(true);
    try {
      await apiPost('/api/v1/ai-training/versions', {
        modelProvider: champion?.modelProvider ?? 'anthropic',
        modelName: champion?.modelName ?? 'claude',
        promptTemplate: prompt,
        playbook: { governance: 'human-approved feedback only', connectWiseIsTruth: true },
        thresholds: {},
        boardPolicies: {},
        changeSummary: summary || 'Prompt edit from training console',
        parentVersionId: champion?.id,
      });
      toast.success('Draft version created — it does not affect anything until published.');
      setEditing(false);
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Draft failed');
    } finally {
      setBusy(false);
    }
  }

  async function publish(v: Version) {
    const run = data?.evaluations.find((e) => e.configVersionId === v.id);
    if (!run) { toast.error('Run offline replay before publishing'); return; }
    if (!confirm('Publish v' + v.version + '? This requires explicit approval and archives the current champion.')) return;
    try {
      await apiPost('/api/v1/ai-training/versions/' + v.id + '/publish', { approval: 'PUBLISH', evaluationRunId: run.id });
      toast.success('Version published');
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Publish failed');
    }
  }

  async function rollback(v: Version) {
    if (!confirm('Rollback by creating a new published copy of v' + v.version + '?')) return;
    try {
      await apiPost('/api/v1/ai-training/versions/' + v.id + '/rollback', { approval: 'ROLLBACK' });
      toast.success('Rollback published as a new version');
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Rollback failed');
    }
  }

  const versionA = data?.versions.find((v) => v.id === compareA);
  const versionB = data?.versions.find((v) => v.id === compareB);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
        <div className="text-sm space-y-1">
          <p><b>Honest limitation:</b> prompt versions are stored, versioned, compared, published and rolled back for real here — but the live triage and copilot runtimes do not yet load the published template at inference time. Publishing records governance state only; a backend follow-up is needed to wire <code>aiConfigVersion.promptTemplate</code> into the runtime prompt builders.</p>
          <p><b>Test-run (dry-run):</b> <span className="italic">Unavailable</span> — there is no backend endpoint that executes a draft prompt against a sample ticket. Building a fake preview would be dishonest, so this button is intentionally absent.</p>
        </div>
      </div>

      <section className="rounded-xl border">
        <div className="p-4 border-b flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold">Prompt template</h2>
            <p className="text-xs text-[hsl(var(--content-subtle))]">The champion is the published version. Editing creates a governed draft, never a live change.</p>
          </div>
          <button onClick={() => startEdit(champion)} disabled={editing} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Edit prompt</button>
        </div>
        <div className="p-4">
          <div className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))] mb-1">Current champion {champion ? '(v' + champion.version + ', ' + champion.modelProvider + ' / ' + champion.modelName + ')' : ''}</div>
          <pre className="max-h-64 overflow-auto rounded-lg bg-[hsl(var(--surface-subtle))] p-3 text-xs whitespace-pre-wrap" data-testid="champion-prompt">{champion?.promptTemplate?.trim() || <span className="italic text-[hsl(var(--content-subtle))]">No published prompt template. Create a draft to add one.</span>}</pre>
        </div>
      </section>

      {editing && (
        <section aria-label="Edit prompt" className="rounded-xl border p-5 space-y-3">
          <h2 className="font-semibold">New draft {champion ? 'from v' + champion.version : ''}</h2>
          <label className="block text-xs font-medium text-[hsl(var(--content-subtle))]" htmlFor="prompt-edit">Prompt template</label>
          <textarea
            id="prompt-edit"
            rows={10}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Write the prompt the AI should follow…"
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 text-sm font-mono"
          />
          <label className="block text-xs font-medium text-[hsl(var(--content-subtle))]" htmlFor="prompt-summary">Why should this change?</label>
          <input
            id="prompt-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Reason for the change (required for the audit trail)"
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-sm"
          />
          <div className="flex gap-2">
            <button onClick={saveDraft} disabled={busy || !prompt.trim() || summary.trim().length < 3} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save draft (does not publish)'}</button>
            <button onClick={() => setEditing(false)} className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm">Cancel</button>
          </div>
        </section>
      )}

      <section className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold flex items-center gap-2"><GitCompare className="h-4 w-4" aria-hidden="true" />Side-by-side compare</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">Pick any two versions to diff their prompt templates.</p>
        </div>
        <div className="p-4 grid gap-3 md:grid-cols-2">
          {(['A', 'B'] as const).map((side) => (
            <div key={side}>
              <label className="block text-xs font-medium text-[hsl(var(--content-subtle))] mb-1" htmlFor={'compare-' + side}>Version {side}</label>
              <select
                id={'compare-' + side}
                value={side === 'A' ? compareA ?? '' : compareB ?? ''}
                onChange={(e) => (side === 'A' ? setCompareA(e.target.value || null) : setCompareB(e.target.value || null))}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-sm"
              >
                <option value="">Select a version…</option>
                {data?.versions.map((v) => <option key={v.id} value={v.id}>v{v.version} · {v.status} · {v.changeSummary.slice(0, 40)}</option>)}
              </select>
            </div>
          ))}
        </div>
        {(versionA || versionB) && (
          <div className="px-4 pb-4 grid gap-3 md:grid-cols-2">
            {[versionA, versionB].map((v, i) => (
              <div key={i} className="rounded-lg border">
                <div className="px-3 py-2 border-b text-xs font-semibold bg-[hsl(var(--surface-subtle))]">{v ? 'v' + v.version + ' · ' + v.status : 'Not selected'}</div>
                <pre className="max-h-56 overflow-auto p-3 text-xs whitespace-pre-wrap">{v?.promptTemplate ?? '—'}</pre>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Version history</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">Drafts never affect production until explicitly published. Publishing archives the previous champion.</p>
        </div>
        <div className="divide-y">
          {data?.versions.map((v) => (
            <div key={v.id} className="p-4 flex flex-wrap items-center gap-4">
              <div className="font-bold">v{v.version}</div>
              <div className="flex-1 min-w-48">
                <div className="text-sm font-medium">{v.modelProvider} / {v.modelName} · <span className="uppercase text-xs">{v.status}</span></div>
                <div className="text-sm text-[hsl(var(--content-subtle))]">{v.changeSummary}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(v)} className="text-sm text-brand-600">Copy to draft</button>
                {v.status === 'draft' && <button onClick={() => publish(v)} className="text-sm flex gap-1 items-center"><Upload className="h-4 w-4" aria-hidden="true" />Publish</button>}
                {v.status === 'archived' && <button onClick={() => rollback(v)} className="text-sm flex gap-1 items-center"><RotateCcw className="h-4 w-4" aria-hidden="true" />Rollback</button>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------ Settings ------------------------------ */

function SettingsTab({ boards, calls }: {
  boards: BoardsQuery;
  calls: CallsQuery;
}) {
  const [savingBoard, setSavingBoard] = React.useState<string | null>(null);
  const [callDraft, setCallDraft] = React.useState<CallSettings | null>(null);
  const [savingCalls, setSavingCalls] = React.useState(false);
  const cs = calls.data;
  React.useEffect(() => { if (cs && !callDraft) setCallDraft({ ...cs }); }, [cs, callDraft]);

  async function saveBoard(p: BoardPolicy, patch: Partial<BoardPolicy>) {
    setSavingBoard(p.id);
    try {
      await apiPatch(`/api/v1/boards/policies/${p.id}`, {
        mode: patch.mode ?? p.mode,
        enabled: patch.enabled ?? p.enabled,
        autoThreshold: patch.autoThreshold ?? p.autoThreshold,
      });
      toast.success((p.boardName || 'Board') + ' policy saved');
      await boards.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingBoard(null);
    }
  }

  async function saveCalls() {
    if (!callDraft) return;
    setSavingCalls(true);
    try {
      await apiPut('/api/v1/calls/settings', {
        matchDecisiveThreshold: callDraft.matchDecisiveThreshold,
        matchRecommendThreshold: callDraft.matchRecommendThreshold,
        matchReviewFloor: callDraft.matchReviewFloor,
        matchMargin: callDraft.matchMargin,
      });
      toast.success('Call match thresholds saved');
      await calls.refetch();
      setCallDraft(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingCalls(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Per-board mode &amp; auto threshold</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">In auto mode the AI acts on its own only when confidence is at or above the threshold. Raise it to make auto mode more conservative.</p>
        </div>
        {boards.loading ? <div className="p-4 text-sm text-[hsl(var(--content-subtle))]">Loading board policies…</div>
          : boards.error ? <div className="p-4 text-sm text-red-600">Unavailable: {boards.error}</div>
            : <div className="divide-y">
              {boards.data?.items.map((p) => (
                <div key={p.id} className="p-4 grid gap-3 md:grid-cols-[1fr_auto_auto] items-center" data-testid={`settings-board-${p.boardName}`}>
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">{p.boardName || 'Board'} <ModeChip mode={p.enabled ? p.mode : 'shadow'} killed={!p.enabled} /></div>
                    <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">{MODE_COPY[p.enabled ? p.mode : 'shadow']}</p>
                  </div>
                  <label className="text-xs">
                    <span className="block mb-1 font-medium text-[hsl(var(--content-subtle))]">Mode</span>
                    <select
                      value={p.mode}
                      disabled={savingBoard === p.id || !p.enabled}
                      onChange={(e) => saveBoard(p, { mode: e.target.value as BoardPolicy['mode'] })}
                      className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1.5 text-sm"
                    >
                      <option value="shadow">Shadow</option>
                      <option value="recommend">Recommend</option>
                      <option value="auto">Auto</option>
                    </select>
                  </label>
                  <label className="text-xs">
                    <span className="block mb-1 font-medium text-[hsl(var(--content-subtle))]">Auto threshold: {Math.round(p.autoThreshold * 100)}%</span>
                    <input
                      type="range"
                      min={0.5}
                      max={1}
                      step={0.01}
                      value={p.autoThreshold}
                      disabled={savingBoard === p.id || p.mode !== 'auto' || !p.enabled}
                      onChange={(e) => saveBoard(p, { autoThreshold: Number(e.target.value) })}
                      className="w-40"
                      aria-label={`Auto threshold for ${p.boardName}`}
                    />
                    <span className="block mt-0.5 text-[11px] text-[hsl(var(--content-subtle))]">AI acts alone only ≥ {Math.round(p.autoThreshold * 100)}% confidence</span>
                  </label>
                </div>
              ))}
            </div>}
      </section>

      <section className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Call-to-ticket match ladder</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">How confident the AI must be before a call match is auto-drafted, recommended, or ignored. The ladder must stay ordered: floor &lt; recommend &lt; decisive.</p>
        </div>
        {calls.loading || !callDraft ? <div className="p-4 text-sm text-[hsl(var(--content-subtle))]">Loading call settings…</div> : (
          <div className="p-4 space-y-4">
            {([
              { key: 'matchDecisiveThreshold' as const, label: 'Decisive threshold', help: 'Calls with explicit ticket evidence auto-draft when confidence is at or above this value (and the margin holds).' },
              { key: 'matchRecommendThreshold' as const, label: 'Recommend threshold', help: 'Contextual matches at or above this confidence are recommended to a reviewer instead of left ambiguous.' },
              { key: 'matchReviewFloor' as const, label: 'Review floor', help: 'Candidates below this confidence are not considered at all — the call stays unmatched.' },
              { key: 'matchMargin' as const, label: 'Required margin', help: 'The top candidate must beat the runner-up by at least this much before it is selected or recommended.' },
            ]).map((f) => (
              <div key={f.key}>
                <div className="flex justify-between text-sm font-medium"><span>{f.label}</span><span className="tabular-nums" data-testid={`preview-${f.key}`}>{callDraft[f.key]?.toFixed(2)}</span></div>
                <input
                  type="range"
                  min={f.key === 'matchMargin' ? 0 : 0}
                  max={f.key === 'matchMargin' ? 0.5 : 1}
                  step={0.01}
                  value={callDraft[f.key] ?? 0}
                  onChange={(e) => setCallDraft({ ...callDraft, [f.key]: Number(e.target.value) })}
                  aria-label={f.label}
                  className="w-full"
                />
                <p className="text-xs text-[hsl(var(--content-subtle))]">{f.help}</p>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button onClick={saveCalls} disabled={savingCalls} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{savingCalls ? 'Saving…' : 'Save thresholds'}</button>
              <span className="text-xs text-[hsl(var(--content-subtle))] flex items-center gap-1"><Info className="h-3.5 w-3.5" aria-hidden="true" />Out-of-order ladders are rejected by the API.</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------------------- Data -------------------------------- */

function DataTab({ data, refetch }: { data: Overview | undefined; refetch: () => unknown }) {
  const from = new Date(Date.now() - 365 * 86400000).toISOString();
  const to = new Date().toISOString();
  const timeEntries = useGet<{ items: TimeCandidate[] }>(`/api/v1/ai-training/time-entry-candidates?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&retentionDays=365&limit=20`);
  const latestEval = data?.evaluations[0];

  async function promote(c: Correction) {
    const capability = c.sourceType === 'dispatch_override' ? 'technician_recommendation' : c.sourceType === 'call_approval' ? 'call_matching' : ((c.kind && labels[c.kind]) ? c.kind : 'summary');
    try {
      await apiPost('/api/v1/ai-training/golden', { sourceType: c.sourceType, sourceId: c.id, capability, boardName: c.ticketCache?.boardName });
      toast.success('Approved example added to golden set');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add example');
    }
  }

  async function promoteTime(c: TimeCandidate) {
    try {
      await apiPost('/api/v1/ai-training/time-entry-golden', { cwTicketId: c.redactedInput.ticket.cwTicketId, cwTimeEntryIds: c.sourceTrace.cwTimeEntryIds, capability: 'actions', retentionDays: c.sourceTrace.retentionDays, reviewDecision: 'APPROVE' });
      toast.success('Reviewed time-entry history added to golden set');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Promotion failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
        <div className="text-sm"><b>Safety boundary:</b> ConnectWise remains the system of record. Only dispatcher/technician-reviewed, redacted examples can enter the golden set. Publishing always requires an admin confirmation and completed offline evaluation.</div>
      </div>

      <section className="rounded-xl border overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="font-semibold flex gap-2 items-center"><FlaskConical className="h-4 w-4" aria-hidden="true" />Accuracy, calibration &amp; drift</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">Champion/challenger offline replay by capability; confidence bins are stored with each run.</p>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(latestEval?.metrics ?? labels).map(([k, v]) => (
            <div key={k} className="rounded-lg bg-[hsl(var(--surface-subtle))] p-3">
              <div className="text-xs text-[hsl(var(--content-subtle))]">{labels[k] ?? k}</div>
              <div className="text-xl font-bold">{typeof v === 'number' ? (v * 100).toFixed(1) + '%' : '—'}</div>
            </div>
          ))}
        </div>
        {latestEval?.drift?.requiresReview && <p className="px-4 pb-4 text-sm text-amber-700">Drift review required against baseline before publish.</p>}
      </section>

      <section className="rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">ConnectWise resolution history</h2>
          <p className="text-xs text-[hsl(var(--content-subtle))]">Primary evidence source: chronological time-entry notes joined to ticket, company, category, technician, outcome and billing context. Entries are redacted, deduplicated, quality-weighted and require reviewer approval.</p>
        </div>
        <div className="divide-y max-h-[32rem] overflow-auto">
          {(timeEntries.data?.items ?? []).map((c) => (
            <div key={c.redactedInput.ticket.cwTicketId} className="p-4 space-y-2">
              <div className="flex gap-3">
                <div className="flex-1">
                  <b>#{c.redactedInput.ticket.cwTicketId} · {c.redactedInput.ticket.summary}</b>
                  <p className="text-xs text-[hsl(var(--content-subtle))]">{c.redactedInput.ticket.company} · {c.redactedInput.ticket.category.join(' / ')} · {c.redactedInput.ticket.status} · weight {c.qualityWeight.toFixed(2)}</p>
                </div>
                <button onClick={() => promoteTime(c)} className="text-xs text-brand-600">Review &amp; add</button>
              </div>
              <div className="space-y-1">
                {c.redactedInput.chronology.slice(0, 4).map((x) => (
                  <div key={x.cwTimeEntryId + String(x.internal)} className="text-xs rounded bg-[hsl(var(--surface-subtle))] p-2">
                    <span className="font-semibold">{x.classification.replaceAll('_', ' ')}</span> · CW #{x.cwTimeEntryId} · {x.billable ? 'billable' : 'non-billable'}
                    {x.observedHours != null && <> · {formatHours(x.observedHours)}</>}
                    {' '}· {x.technician?.identifier ?? 'unknown tech'}
                    <p>{x.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-amber-700">{c.correctnessWarning} Retained {c.sourceTrace.retentionDays} days; traceable to {c.sourceTrace.cwTimeEntryIds.length} CW entries.</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <h2 className="font-semibold flex gap-2"><ShieldCheck className="h-4 w-4" aria-hidden="true" />Corrections queue</h2>
            <p className="text-xs text-[hsl(var(--content-subtle))]">Existing triage reviews, dispatch overrides and call approvals — no duplicate review flow.</p>
          </div>
          <div className="divide-y max-h-96 overflow-auto">
            {data?.corrections.slice(0, 30).map((c) => (
              <div key={c.sourceType + c.id} className="p-3 flex gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">{c.sourceType.replaceAll('_', ' ')} {c.ticketCache?.cwTicketId ? '· #' + c.ticketCache.cwTicketId : ''}</div>
                  <div className="text-xs text-[hsl(var(--content-subtle))]">{c.overrideReason || c.reason || c.redactedPreview || c.status}</div>
                </div>
                <button onClick={() => promote(c)} className="text-xs text-brand-600">Add to golden</button>
              </div>
            ))}
            {!data?.corrections.length && <p className="p-6 text-sm text-[hsl(var(--content-subtle))]">No corrections waiting.</p>}
          </div>
        </div>
        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <h2 className="font-semibold flex gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />Suggested rule changes</h2>
            <p className="text-xs text-[hsl(var(--content-subtle))]">Suggestions are advisory and require a new draft plus evaluation.</p>
          </div>
          {data?.suggestions.length ? (
            <div className="divide-y">
              {data.suggestions.map((s) => (
                <div className="p-3" key={s.id}>
                  <b className="text-sm">{labels[s.capability] ?? s.capability}</b>
                  <p className="text-sm">{s.suggestion}</p>
                  <span className="text-xs text-[hsl(var(--content-subtle))]">{s.boardName ?? 'All boards'}</span>
                </div>
              ))}
            </div>
          ) : <p className="p-6 text-sm text-[hsl(var(--content-subtle))]">No pending rule suggestions.</p>}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl border p-4"><div className="text-xs text-[hsl(var(--content-subtle))]">{label}</div><div className="font-bold mt-1">{value}</div></div>;
}
